<template>
  <div class="snow-input">
    <div
      class="snow-input__container"
      :class="{ filled: !!innerValue }"
      @click="onClick"
    >
      <div class="snow-input__inner">
        <label v-if="label" :for="id">
          {{ label }}
        </label>

        <textarea
          v-if="type == 'textarea'"
          v-bind="bindings"
          v-model="innerValue"
          v-on="listeners"
        />
        <the-mask
          v-else-if="mask"
          v-bind="bindings"
          v-model="innerValue"
          v-on="listeners"
          :mask="mask"
          :masked="masked"
        />
        <input v-else v-bind="bindings" v-model="innerValue" v-on="listeners" />
      </div>

      <slot>
        <snow-button
          v-if="type === 'password'"
          size="sm"
          class="text-dark-40"
          variant="borderless"
          :icon="show ? 'ph ph-eye-slash' : 'ph ph-eye'"
          @click="show = !show"
        />
        <i v-else-if="_icon" :class="_icon" />
      </slot>
    </div>

    <span class="snow-input__error" v-if="error">
      {{ error }}
    </span>
  </div>
</template>

<script>
import { uniqueId } from "lodash";

let timeout;
export default {
  props: {
    value: {
      type: [String, Number]
    },
    id: {
      type: String,
      default: uniqueId("snow-input-")
    },
    label: {
      type: String
    },
    errors: {
      type: [Array, String]
    },
    rows: {
      type: [Number, String],
      default: 2
    },
    disabled: {
      type: Boolean,
      default: false
    },
    delay: {
      type: Number,
      default: 0
    },
    rules: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: "text"
    },
    icon: {
      type: String
    },
    mask: {
      type: String
    },
    masked: {
      type: Boolean
    },
    readonly: {
      type: Boolean
    }
  },
  data() {
    return {
      innerValue: this.value,
      error: null,
      show: false,
      check: false
    };
  },
  methods: {
    onKeyDown(event) {
      this.error = null;
      this.check = false;
      if (this.type == "number") {
        const invalidKeyCodes = [69, 190, 188, 229];
        if (invalidKeyCodes.includes(event.keyCode)) event.preventDefault();
      }
    },
    onClick() {
      if (this._disabled || this.readonly) return;
      if (this.mask) this.$refs.input.$el.focus();
      else this.$refs.input.focus();
    },
    onBlur() {
      this.$emit("blur", this.innerValue);
      this.validate();
    },

    validate() {
      if (this.rules.length) {
        const err = this.rules.find(rule => rule(this.innerValue) !== true);
        if (err) this.error = err(this.innerValue);
        else {
          this.check = true;
        }
      }
    }
  },
  computed: {
    _disabled() {
      return !!this.disabled;
    },
    bindings() {
      const bindings = {
        disabled: this._disabled,
        readonly: this.readonly,
        ref: "input"
      };
      if (this.type !== "textarea") {
        let type = this.type === "password" && this.show ? "text" : this.type;

        bindings.type = type;
      } else {
        bindings.rows = this.rows;
      }

      return bindings;
    },
    listeners() {
      return {
        blur: this.onBlur,
        keydown: this.onKeyDown
      };
    },
    _icon() {
      if (this.error) return ["ph", "ph-warning", "text-red", "fs-20"];

      if (this.check) return ["ph", "ph-check", "text-green", "fs-20"];

      return [this.icon, "text-dark-40", "fs-24"];
    }
  },
  watch: {
    value(newVal) {
      this.innerValue = newVal;
    },
    innerValue(newVal) {
      if (newVal != this.value) {
        this.$emit("input", newVal);

        clearTimeout(timeout);
        timeout = setTimeout(() => {
          this.$emit("change", newVal);
        }, this.delay);
      }
    }
  },
  mounted() {
    if (this.mask) {
      const el = this.$refs.input.$el;
      el.addEventListener("blur", this.onBlur);
      el.addEventListener("keydown", this.onKeyDown);
    }
  }
};
</script>
<style lang="scss" scoped>
.snow-input {
  --transition: 0.2s all ease-in-out;

  .snow-input__container {
    align-items: center;
    background: var(--light);
    border: 0.5px solid var(--dark-10);
    border-radius: 16px;
    cursor: text;
    display: flex;
    padding: 16px 20px;
    transition: var(--transition);
    width: 100%;

    .snow-input__inner {
      flex: 1;
      transition: var(--transition);

      input,
      textarea {
        background: transparent;
        border: none;
        color: var(--dark);
        font-size: 14px;
        line-height: 20px;
        min-height: 0;
        outline: none;
        overflow: hidden;
        padding: 0;
        width: 100%;
      }
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        margin-bottom: 4px;
      }
      textarea {
        resize: none;
      }
    }
    &:has(label) {
      align-items: end;
      .snow-input__inner {
        display: grid;
        grid-template-rows: auto 0fr;
      }

      label {
        color: var(--dark-40);
        cursor: text;
        font-size: 14px;
        line-height: 20px;
        margin: 0;
        padding: 2px 0;
        transition: var(--transition);
        white-space: nowrap;
      }

      &:focus-within,
      &.filled {
        .snow-input__inner {
          grid-template-rows: auto 1fr;
        }
        label {
          padding: 6px 0;
          font-size: 12px;
          line-height: 16px;
        }
      }
    }

    &:hover,
    &:focus-within {
      border-color: var(--dark-40);
      background-color: var(--bg-5);
    }
    &:focus-within {
      box-shadow: 0px 0px 0px 4px rgba(0, 0, 0, 0.05);
    }
  }

  .snow-input__error {
    color: var(--red);
    font-size: 12px;
  }
  &:has(.snow-input__error) {
    .snow-input__container {
      border-color: var(--red);
      margin-bottom: 4px;
    }
  }

  &:has(input:read-only, textarea:read-only) {
    .snow-input__container {
      background-color: var(--dark-4);
      border-color: transparent;
      &:focus-within {
        box-shadow: none;
      }
      cursor: default;
      * {
        cursor: default;
      }
    }
  }

  &:has(input:disabled, textarea:disabled) {
    .snow-input__container {
      background-color: var(--dark-4);
      border-color: transparent;
      cursor: not-allowed;
      * {
        cursor: not-allowed;
      }
      input,
      textarea,
      label {
        color: var(--dark-10);
      }
    }
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px var(--light) inset !important;
  }
}
</style>
